// Form Component to Add or Edit Config Group

import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, Select } from "antd";
import FormLayout, {
  FormContent,
  FormFooter,
  FormHeader,
} from "../../../components/form/form";
import { FormRow } from "../../../components/form/form-row";
import { eResultCode } from "../../../utils/enum";
import useFetch from "../../../hooks/useFetch";
import useToast from "../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../state/toast/slice";
import { AddEditProduct } from "../../../utils/api.constant";
import { TProduct } from "../../../types";
import { requiredFieldValidator } from "../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";

type TDrawerProps = {
  id: number;
  onCloseDrawer: () => void;
  onRefreshList: () => void;
  productName?: string; // Now included
  productCode?: string; // Now included
  gyro?: number;
};

/**
 * Form Component to Add or Edit Config Group
 *
 * @param {DrawerProps} props - drawer props
 * @returns {JSX.Element}
 */
export default function ConfigProductForm(props: TDrawerProps) {
  const [form] = Form.useForm(); // Form instance
  const [isLoading, setIsLoading] = useState<boolean>(false); // Loading state
  const { post } = useFetch(); // Hook to make API calls
  const { onShowToast } = useToast(); // Hook to show toast

  // default values initial state
  const defaultValues: TProduct = {
    id: 0,
    productName: "",
    machineTypeName: "",
    productCode: "",
    gyro: 0,
  };

  // Handle on submit event
  const onFinish = async (values: TProduct) => {
    try {
      const payload = {
        data: {
          ...values,
          id: props.id ? props.id : 0,
        },
      };
      setIsLoading(true);

      const response = await post(AddEditProduct, payload);
      if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
        onShowToast({
          type: ToastType.success,
          title: <CheckOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
        form.resetFields();
        props.onRefreshList();
        props.onCloseDrawer();
      } else {
        onShowToast({
          type: ToastType.error,
          title: <CloseOutlined />,
          position: ToastOpen.centerTop,
          content: response.dataResponse.description,
        });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      productName: props.productName || "",
      productCode: props.productCode || "",
      gyro: props.gyro,
    });
  }, [props]);

  return (
    <FormLayout>
      <Form
        form={form}
        name="register"
        onFinish={onFinish}
        initialValues={defaultValues}
        layout="vertical"
        autoComplete="off"
      >
        <FormHeader>
          <CloseOutlined
            style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
            onClick={() => {
              props.onCloseDrawer();
            }}
          />
        </FormHeader>

        <FormContent>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="productName"
                label="Product Name"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter product name"),
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  placeholder="Enter the product name"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16} className="formRowHeight">
            <Col span={24}>
              <Form.Item
                name="productCode"
                label="Product Code"
                rules={[
                  {
                    required: true,
                    validator: requiredFieldValidator("enter product code"),
                  },
                ]}
              >
                <Input
                  autoComplete="off"
                  placeholder="Enter the product code"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="gyro"
                label="Gyro"
                rules={[
                  { required: true, message: "Please select Gyro status" },
                ]}
              >
                <Select
                  placeholder="Select Gyro status"
                  options={[
                    { label: "Yes", value: 1 },
                    { label: "No", value: 0 },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormContent>
        <FormFooter className="formFooter">
          <FormRow className="formRow">
            <Button
              type="primary"
              className="btnDimensions"
              onClick={form.submit}
              disabled={isLoading}
            >
              Save
            </Button>
            <Button
              type="primary"
              disabled={isLoading}
              className="btnDimensions"
              onClick={props.onCloseDrawer}
            >
              Cancel
            </Button>
          </FormRow>
        </FormFooter>
      </Form>
    </FormLayout>
  );
}
